// @flow

import React from 'react'
import styled, { css } from 'styled-components'
import { FiShoppingBag } from 'react-icons/fi'

import { WebshopContext } from '../../../../shopV2/shared'
import { SessionContext } from '../../../../shared'
import { Submenu, SubmenuNavigationLinkStyle } from '../shared'

type Props = {
  isMobile: boolean,
  setShowManualEditCustomerModal: Function,
  setShowRetailerSelector: Function,
  setShowSessionListModal: Function,
}

const NavigationRightSideWebshopSessionManager = ({
  isMobile,
  setShowManualEditCustomerModal,
  setShowRetailerSelector,
  setShowSessionListModal,
}: Props) => {
  const { availableBrand, updateWebshopSession, webshopSession } =
    React.useContext(WebshopContext)
  const { entity } = React.useContext(SessionContext)

  const [isSubmenuOpen, setIsSubmenuOpen] = React.useState(false)

  const availableConnections = availableBrand
    ? availableBrand.retailer_connections.filter(
        connection => connection.b2b_access
      )
    : []

  const customerCanMakeDropShipping = React.useMemo(() => {
    return webshopSession.customer
      ? webshopSession.customer.dropshipping
      : false
  }, [webshopSession.customer])

  const onConvertToDropshippingOrder = React.useCallback(() => {
    return updateWebshopSession({
      dropshipping: true,
    })
  }, [updateWebshopSession])

  const onConvertToNormalOrder = React.useCallback(() => {
    return updateWebshopSession({
      dropshipping: false,
    })
  }, [updateWebshopSession])

  const buttonLabelParts = []
  if (entity.entity_type === 'brand') {
    buttonLabelParts.push(
      <div>
        {!isMobile && <>Customer: </>}
        <ButtonLabelHighlight>
          {webshopSession.customer.name}
        </ButtonLabelHighlight>
      </div>
    )
  }
  if (entity.entity_type === 'shop' && availableConnections.length > 1) {
    buttonLabelParts.push(
      <div>
        {!isMobile && <>Shopping as: </>}
        <ButtonLabelHighlight>
          {webshopSession.customer.name}
        </ButtonLabelHighlight>
      </div>
    )
  }
  if (entity.entity_type === 'shop' && availableConnections.length === 1) {
    buttonLabelParts.push(<div>Current open order</div>)
  }

  if (customerCanMakeDropShipping && webshopSession.dropshipping) {
    buttonLabelParts.push(
      <div>
        <ButtonLabelHighlight>Dropshipping</ButtonLabelHighlight>
      </div>
    )
  }
  if (webshopSession.drop) {
    buttonLabelParts.push(
      <div>
        {!isMobile && <>Drop: </>}
        <ButtonLabelHighlight>{webshopSession.drop.name}</ButtonLabelHighlight>
      </div>
    )
  }
  if (webshopSession.drop_filter_code) {
    buttonLabelParts.push(
      <div>
        {!isMobile && <>Drop code: </>}
        <ButtonLabelHighlight>
          {webshopSession.drop_filter_code}
        </ButtonLabelHighlight>
      </div>
    )
  }
  if (webshopSession.subbrand) {
    buttonLabelParts.push(
      <div>
        {!isMobile && <>Brand: </>}
        <ButtonLabelHighlight>
          {webshopSession.subbrand.name}
        </ButtonLabelHighlight>
      </div>
    )
  }

  return (
    <WebshopSessionManagerMenuContainer isSubmenuOpen={isSubmenuOpen}>
      <WebshopSessionManagerButton
        type="button"
        onClick={() => {
          // On desktop the submenu opens on hover, so we only needs this
          // opening via onClick on mobile
          if (isMobile) {
            setIsSubmenuOpen(!isSubmenuOpen)
          }
        }}
      >
        <StyledFiShoppingBagIcon />

        {buttonLabelParts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <ButtonLabelPartsSeparator>/</ButtonLabelPartsSeparator>
            )}
            {part}
          </React.Fragment>
        ))}

        <CaretIconWrapper>
          <span className="caret" />
        </CaretIconWrapper>
      </WebshopSessionManagerButton>

      <Submenu side="left">
        {entity.entity_type === 'brand' && (
          <SubmenuButton
            type="button"
            onClick={() => {
              setShowManualEditCustomerModal(true)
              setIsSubmenuOpen(false)
            }}
          >
            Change customer
          </SubmenuButton>
        )}
        {entity.entity_type === 'shop' && availableConnections.length > 1 && (
          <SubmenuButton
            type="button"
            onClick={() => {
              setShowRetailerSelector(true)
              setIsSubmenuOpen(false)
            }}
          >
            Change customer to shop as
          </SubmenuButton>
        )}

        {customerCanMakeDropShipping && !webshopSession.dropshipping && (
          <SubmenuButton
            type="button"
            onClick={() => {
              onConvertToDropshippingOrder()
              setIsSubmenuOpen(false)
            }}
          >
            Convert to dropshipping order
          </SubmenuButton>
        )}
        {customerCanMakeDropShipping && webshopSession.dropshipping && (
          <SubmenuButton
            type="button"
            onClick={() => {
              onConvertToNormalOrder()
              setIsSubmenuOpen(false)
            }}
          >
            Convert from dropshipping to normal order
          </SubmenuButton>
        )}

        <SubmenuButton
          type="button"
          onClick={() => {
            setShowSessionListModal(true)
            setIsSubmenuOpen(false)
          }}
        >
          Manage open orders
        </SubmenuButton>
      </Submenu>
    </WebshopSessionManagerMenuContainer>
  )
}

export default NavigationRightSideWebshopSessionManager

const WebshopSessionManagerMenuContainer = styled.div`
  height: 100%;
  position: relative;

  &:hover ${Submenu} {
    display: block;
  }

  ${({ isSubmenuOpen }) =>
    isSubmenuOpen &&
    css`
      ${Submenu} {
        display: block;
      }
    `}
`

const WebshopSessionManagerButton = styled.button`
  height: 100%;
  padding: 0 15px;
  margin: 0;
  border: none;
  background: transparent;
  font-size: 12px;
  line-height: 1;

  display: flex;
  align-items: center;
`

const ButtonLabelPart = styled.div``

const ButtonLabelHighlight = styled.span`
  font-weight: bold;
`

const ButtonLabelPartsSeparator = styled.div`
  padding: 0 6px;
`

const StyledFiShoppingBagIcon = styled(FiShoppingBag).attrs({
  size: 16,
})`
  margin-right: 7px;

  /* Move the icon 1px up as it is otherwise not visually centered with the text */
  position: relative;
  top: -1px;
`

const CaretIconWrapper = styled.span`
  padding-left: 4px;
`

const SubmenuButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  background: transparent;
  border: none;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;

  ${SubmenuNavigationLinkStyle};
`
